var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { cva } from "class-variance-authority";
import { Check, Circle, X } from "lucide-react";
import { cn } from "@/lib/utils";
var timelineVariants = cva("grid", {
    variants: {
        positions: {
            left: "[&>li]:grid-cols-[0_min-content_1fr]",
            right: "[&>li]:grid-cols-[1fr_min-content]",
            center: "[&>li]:grid-cols-[1fr_min-content_1fr]",
        },
    },
    defaultVariants: {
        positions: "left",
    },
});
var Timeline = React.forwardRef(function (_a, ref) {
    var children = _a.children, className = _a.className, positions = _a.positions, props = __rest(_a, ["children", "className", "positions"]);
    return (_jsx("ul", __assign({ className: cn(timelineVariants({ positions: positions }), className), ref: ref }, props, { children: children })));
});
Timeline.displayName = "Timeline";
var timelineItemVariants = cva("grid items-center gap-x-2", {
    variants: {
        status: {
            done: "text-primary",
            default: "text-muted-foreground",
        },
    },
    defaultVariants: {
        status: "default",
    },
});
var TimelineItem = React.forwardRef(function (_a, ref) {
    var className = _a.className, status = _a.status, props = __rest(_a, ["className", "status"]);
    return (_jsx("li", __assign({ className: cn(timelineItemVariants({ status: status }), className), ref: ref }, props)));
});
TimelineItem.displayName = "TimelineItem";
var timelineDotVariants = cva("col-start-2 col-end-3 row-start-1 row-end-1 flex size-7 items-center justify-center rounded-full border-2 border-[#D1D1D1]", {
    variants: {
        status: {
            default: "[&>*]:hidden",
            current: "border-[#034EA2] [&>*:not(.lucide-circle)]:hidden [&>.lucide-circle]:fill-current [&>.lucide-circle]:text-[#034EA2]",
            done: "border-[#022A59] bg-[#034EA2] [&>*:not(.lucide-check)]:hidden [&>.lucide-check]:text-[#FEFEFE]",
            error: "border-destructive bg-destructive border-[#8a0404] [&>*:not(.lucide-x)]:hidden [&>.lucide-x]:text-[#8a0404]",
            custom: "[&>*:not(:nth-child(4))]:hidden [&>*:nth-child(4)]:block",
        },
    },
    defaultVariants: {
        status: "default",
    },
});
var TimelineDot = React.forwardRef(function (_a, ref) {
    var className = _a.className, status = _a.status, customIcon = _a.customIcon, props = __rest(_a, ["className", "status", "customIcon"]);
    return (_jsxs("div", __assign({ role: "status", className: cn("timeline-dot", timelineDotVariants({ status: status }), className), ref: ref }, props, { children: [_jsx(Circle, { className: "size-3" }), _jsx(Check, { className: "size-4" }), _jsx(X, { className: "size-4" }), customIcon] })));
});
TimelineDot.displayName = "TimelineDot";
var timelineContentVariants = cva("row-start-2 row-end-2 sm:pl-6 pb-8 text-muted-foreground text-xs", {
    variants: {
        side: {
            right: "col-start-3 col-end-4 mr-auto text-left",
            left: "col-start-1 col-end-2 ml-auto text-right",
        },
    },
    defaultVariants: {
        side: "right",
    },
});
var TimelineContent = React.forwardRef(function (_a, ref) {
    var className = _a.className, side = _a.side, props = __rest(_a, ["className", "side"]);
    return (_jsx("div", __assign({ className: cn(timelineContentVariants({ side: side }), className), ref: ref }, props)));
});
TimelineContent.displayName = "TimelineContent";
var timelineHeadingVariants = cva("row-start-1 row-end-1 sm:sm:pl-6 line-clamp-1 max-w-full truncate", {
    variants: {
        side: {
            right: "col-start-3 col-end-4 mr-auto text-left",
            left: "col-start-1 col-end-2 ml-auto text-right",
        },
        variant: {
            primary: "text-base font-medium text-primary",
            secondary: "text-sm font-light text-muted-foreground",
        },
    },
    defaultVariants: {
        side: "right",
        variant: "primary",
    },
});
var TimelineHeading = React.forwardRef(function (_a, ref) {
    var className = _a.className, side = _a.side, variant = _a.variant, props = __rest(_a, ["className", "side", "variant"]);
    return (_jsx("p", __assign({ role: "heading", "aria-level": variant === "primary" ? 2 : 3, className: cn(timelineHeadingVariants({ side: side, variant: variant }), className), ref: ref }, props)));
});
TimelineHeading.displayName = "TimelineHeading";
var TimelineLine = React.forwardRef(function (_a, ref) {
    var className = _a.className, _b = _a.done, done = _b === void 0 ? false : _b, props = __rest(_a, ["className", "done"]);
    return (_jsx("hr", __assign({ role: "separator", "aria-orientation": "vertical", className: cn("col-start-2 col-end-3 row-start-2 row-end-2 mx-auto flex h-5/6  w-0.5 justify-center rounded-full py-1", done ? "bg-[#022A59]" : "bg-[#D1D1D1]", className), ref: ref }, props)));
});
TimelineLine.displayName = "TimelineLine";
export { Timeline, TimelineDot, TimelineItem, TimelineContent, TimelineHeading, TimelineLine, };
